import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LeaveManagementApi } from '@/services/leave-management.api';
import { AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

/** School tier — Leave Report, one staff member's month-by-month leave balance. Matches real legacy's `/leave/report` ("Manage Leave Details") exactly. */
export function LeaveReportPage() {
  const [userId, setUserId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');

  const { data: staffOptions = [] } = useQuery({ queryKey: ['leave-staff-options'], queryFn: () => LeaveManagementApi.staffOptions() });
  const { data: academicYears = [] } = useQuery({
    queryKey: ['leave-academic-years'],
    queryFn: () => AcademicYearsApi.list({ limit: 100 }).then((r) => r.items),
  });

  // Default to the current session year once it loads, matching real
  // legacy's own report — the backend already defaults to it when omitted,
  // but the dropdown should visibly show it selected, not blank.
  useEffect(() => {
    if (academicYearId || !academicYears.length) return;
    const current = academicYears.find((y) => y.isCurrent);
    if (current) setAcademicYearId(current.id);
  }, [academicYears, academicYearId]);

  const { data: report, isFetching } = useQuery({
    queryKey: ['staff-leave-report', userId, academicYearId],
    queryFn: () => LeaveManagementApi.report(userId, academicYearId || undefined),
    enabled: !!userId,
  });

  return (
    <div>
      <PageHeader title="Manage Leave Details" description="A staff member's month-by-month leave allocation and usage." />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select className="w-56" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Select Staff</option>
          {staffOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name ?? '—'}
            </option>
          ))}
        </Select>
        <Select className="w-40" value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
          <option value="">Session Year: Current</option>
          {academicYears.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      {!userId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a staff member to view their leave details.</div>
      ) : isFetching && !report ? (
        <div className="card p-8 text-center text-sm text-slate-400">Loading…</div>
      ) : !report?.configured ? (
        <div className="card p-8 text-center text-sm text-slate-400">
          No leave settings configured for the current academic year — nothing to report yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  No.
                </th>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  Month
                </th>
                <th rowSpan={2} className="px-4 py-2 text-left align-bottom font-medium text-slate-600">
                  Allocated
                </th>
                <th colSpan={3} className="border-l border-slate-200 px-4 py-1.5 text-center font-medium text-slate-600">
                  Used
                </th>
                <th colSpan={2} className="border-l border-slate-200 px-4 py-1.5 text-center font-medium text-slate-600">
                  Remaining
                </th>
              </tr>
              <tr>
                <th className="border-l border-slate-200 px-4 py-2 text-left font-medium text-slate-600">CL (Casual Leave)</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">LWP (Leave Without Pay)</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Total</th>
                <th className="border-l border-slate-200 px-4 py-2 text-left font-medium text-slate-600">CL</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.months.map((m, i) => (
                <tr key={m.month} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{m.month}</td>
                  <td className="px-4 py-2 text-slate-600">{m.allocated}</td>
                  <td className="border-l border-slate-100 px-4 py-2 text-slate-600">{m.usedCl || '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{m.usedLwp || '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{m.usedTotal || '-'}</td>
                  <td className="border-l border-slate-100 px-4 py-2 text-slate-600">{m.remainingCl}</td>
                  <td className="px-4 py-2 text-slate-600">{m.remainingTotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
